import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Image, Spinner, Alert } from 'react-bootstrap';
import { BASE_URL, UPLOAD_BASE_URL } from '../services/api';

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Using your API_BASE_URL
      const response = await fetch(`${BASE_URL}/public/about.php?action=getAll`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setAboutData(result.data);
      } else {
        setError(result.message || 'Failed to fetch about data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading about information: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full image URL using your UPLOAD_BASE_URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/500x300';
    
    // Check if it's already a full URL
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Remove 'uploads/' from path if it exists and prepend with UPLOAD_BASE_URL
    // Your UPLOAD_BASE_URL is 'http://localhost/college-website/backend'
    // Image paths in DB are like 'uploads/about/logo_xxxx.png'
    // So we need to go up one directory from 'backend'
    
    if (imagePath.startsWith('uploads/')) {
      // Go up one level from 'backend' to 'college-website'
      const baseUrl = UPLOAD_BASE_URL;
      return `${baseUrl}/${imagePath}`;
    }
    
    // If path doesn't start with 'uploads/', use as is
    return `${UPLOAD_BASE_URL}/${imagePath}`;
  };

  // Alternative: Use logo_url and campus_image_url from API if available
  const getLogoUrl = () => {
    if (!aboutData) return '';
    
    // If API already provides full URL, use it
    if (aboutData.logo_url) return aboutData.logo_url;
    
    // Otherwise construct it
    if (aboutData.logo) {
      return getImageUrl(aboutData.logo);
    }
    
    return 'https://via.placeholder.com/300x150?text=College+Logo';
  };

  const getCampusImageUrl = () => {
    if (!aboutData) return 'https://via.placeholder.com/500x300?text=Campus+Image';
    
    // If API already provides full URL, use it
    if (aboutData.campus_image_url) return aboutData.campus_image_url;
    
    // Otherwise construct it
    if (aboutData.campus_image) {
      return getImageUrl(aboutData.campus_image);
    }
    
    return 'https://via.placeholder.com/500x300?text=Campus+Image';
  };

  // Helper function to process objectives into array
  const getObjectivesList = () => {
    if (!aboutData) return [];
    
    // Use objectives_list from API if available
    if (aboutData.objectives_list && Array.isArray(aboutData.objectives_list)) {
      return aboutData.objectives_list;
    }
    
    // Fallback: parse from text
    if (aboutData.objectives) {
      return aboutData.objectives
        .split('\n')
        .filter(obj => obj.trim() !== '')
        .map(obj => obj.replace(/^\d+\.\s*/, '').trim())
        .filter(obj => obj.length > 0);
    }
    
    return [];
  };

  // Helper function to process achievements into array
  const getAchievementsList = () => {
    if (!aboutData) return [];
    
    // Use achievements_list from API if available
    if (aboutData.achievements_list && Array.isArray(aboutData.achievements_list)) {
      return aboutData.achievements_list;
    }
    
    // Fallback: parse from text
    if (aboutData.achievements) {
      return aboutData.achievements
        .split('\n')
        .filter(ach => ach.trim() !== '')
        .map(ach => ach.replace(/^[•\-*]\s*/, '').trim())
        .filter(ach => ach.length > 0);
    }
    
    return [];
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ height: '80px' }}></div>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading about information...</p>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ height: '80px' }}></div>
        <Container className="py-5">
          <Alert variant="danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={fetchAboutData}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
            </div>
          </Alert>
        </Container>
      </>
    );
  }

  if (!aboutData) {
    return (
      <>
        <div style={{ height: '80px' }}></div>
        <Container className="py-5">
          <Alert variant="warning">
            <i className="fas fa-info-circle me-2"></i>
            No about data available. Please try again later.
          </Alert>
        </Container>
      </>
    );
  }

  const objectives = getObjectivesList();
  const achievements = getAchievementsList();

  return (
    <>
      {/* Navbar Spacer */}
      <div style={{ height: '80px' }}></div>
      
      <Container className="py-5" style={{ paddingTop: '40px' }}>
        
        {/* College Title and Description */}
        <div className="text-center mb-5">
          <h1 className="display-4 mb-3 fw-bold text-primary">
            {aboutData.title || 'About Our College'}
          </h1>
          <p className="lead text-muted max-w-800 mx-auto">
            {aboutData.description || 'We are a premier educational institution committed to excellence in education, research, and community service.'}
          </p>
        </div>
        
        {/* Main Content Row */}
        <Row className="mb-5 align-items-center">
          {/* Images Section */}
          <Col lg={5} className="mb-4 mb-lg-0">
            {/* Logo */}
            <div className="text-center mb-4">
              <div className="bg-light p-4 rounded shadow-sm d-inline-block">
                <Image 
                  src={getLogoUrl()}
                  alt="College Logo" 
                  fluid
                  className="img-fluid"
                  style={{ maxHeight: '120px', width: 'auto' }}
                />
              </div>
              <p className="text-muted mt-2 small">Official Logo</p>
            </div>
            
            {/* Campus Image */}
            <div className="text-center">
              <Image 
                src={getCampusImageUrl()}
                alt="College Campus" 
                fluid
                className="img-fluid rounded shadow-lg"
                style={{ 
                  maxHeight: '300px', 
                  width: '100%', 
                  objectFit: 'cover',
                  border: '5px solid white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
              <p className="text-muted mt-2 small">Our Campus</p>
            </div>
          </Col>
          
          {/* Welcome Section */}
          <Col lg={7}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-4">
                <h2 className="mb-4 border-bottom pb-3">
                  <i className="fas fa-university text-primary me-2"></i>
                  Welcome to Our Institution
                </h2>
                
                {/* History */}
                {aboutData.history && (
                  <div className="mb-4">
                    <h5 className="text-secondary">
                      <i className="fas fa-history me-2"></i>
                      Our History
                    </h5>
                    <p className="mb-0">{aboutData.history}</p>
                  </div>
                )}
                
                {/* Leadership Message */}
                {aboutData.leadership_message && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <h5 className="text-primary">
                      <i className="fas fa-quote-left me-2"></i>
                      Message from Leadership
                    </h5>
                    <p className="mb-0 mt-2 fst-italic">
                      "{aboutData.leadership_message}"
                    </p>
                    <p className="text-end text-muted mt-2 small mb-0">- College Leadership</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Vision & Mission Cards */}
        <h2 className="text-center mb-4 section-title">
          <span className="bg-primary text-white px-4 py-2 rounded">
            Our Vision & Mission
          </span>
        </h2>
        
        <Row className="mb-5">
          <Col md={6} className="mb-4">
            <Card className="h-100 shadow-lg border-0 mission-vision-card">
              <Card.Body className="p-4">
                <div className="text-center mb-3">
                  <div className="vision-icon bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <i className="fas fa-eye fa-2x"></i>
                  </div>
                </div>
                <Card.Title className="text-center text-primary h4 mb-3">
                  Our Vision
                </Card.Title>
                <Card.Text className="text-center fs-5">
                  {aboutData.vision || 'To be a globally recognized institution of excellence in education, research, and innovation.'}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6} className="mb-4">
            <Card className="h-100 shadow-lg border-0 mission-vision-card">
              <Card.Body className="p-4">
                <div className="text-center mb-3">
                  <div className="mission-icon bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <i className="fas fa-bullseye fa-2x"></i>
                  </div>
                </div>
                <Card.Title className="text-center text-success h4 mb-3">
                  Our Mission
                </Card.Title>
                <Card.Text className="text-center fs-5">
                  {aboutData.mission || 'To provide quality education through innovative teaching-learning methods.'}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Objectives Section */}
        {objectives.length > 0 && (
          <>
            <h2 className="text-center mb-4 section-title">
              <span className="bg-info text-white px-4 py-2 rounded">
                Our Objectives
              </span>
            </h2>
            
            <Row className="mb-5">
              <Col>
                <Card className="shadow-sm border-0">
                  <Card.Body className="p-4">
                    <Row>
                      {objectives.map((objective, index) => (
                        <Col md={6} lg={4} key={index} className="mb-3">
                          <div className="d-flex align-items-start p-3 bg-light rounded h-100">
                            <div className="objective-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                 style={{ width: '35px', height: '35px', flexShrink: 0 }}>
                              {index + 1}
                            </div>
                            <span>{objective}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Achievements Section */}
        {achievements.length > 0 && (
          <>
            <h2 className="text-center mb-4 section-title">
              <span className="bg-warning text-dark px-4 py-2 rounded">
                Our Achievements
              </span>
            </h2>
            
            <Row className="mb-5">
              <Col>
                <Card className="shadow-sm border-0">
                  <Card.Body className="p-4">
                    <Row>
                      {achievements.map((achievement, index) => (
                        <Col md={6} lg={4} key={index} className="mb-3">
                          <div className="d-flex align-items-center p-3 bg-light rounded h-100">
                            <div className="achievement-icon text-warning me-3">
                              <i className="fas fa-trophy fa-lg"></i>
                            </div>
                            <span>{achievement}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Last Updated Info */}
        <div className="text-center text-muted mt-5 pt-5 border-top">
          <p className="small">
            <i className="fas fa-clock me-1"></i>
            Last updated: {formatDate(aboutData.updated_at)}
          </p>
        </div>
      </Container>

      {/* Inline CSS styles */}
      <style jsx="true">{`
        /* Solution 1: Global body padding */
        body {
          padding-top: 20px !important;
        }
        
        /* Solution 2: Container-specific styling */
        .container.py-5 {
          margin-top: 20px !important;
        }
        
        /* Solution 3: Ensure navbar has proper z-index */
        .navbar {
          z-index: 1060 !important;
          position: fixed !important;
          top: 0 !important;
          width: 100% !important;
          height: 80px !important;
        }
        
        /* Additional safety styles */
        * {
          box-sizing: border-box;
        }
        
        /* Make sure content doesn't hide under navbar */
        @media (max-width: 768px) {
          body {
            padding-top: 60px !important;
          }
          .container.py-5 {
            padding-top: 20px !important;
          }
        }

        /* Custom styles for About page */
        .max-w-800 {
          max-width: 800px;
        }
        
        .mission-vision-card {
          transition: all 0.3s ease;
          border-top: 4px solid #0d6efd !important;
        }
        
        .mission-vision-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
        }
        
        .mission-vision-card:nth-child(2) {
          border-top-color: #198754 !important;
        }
        
        .section-title {
          position: relative;
          padding: 20px 0;
        }
        
        .section-title::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #dee2e6, transparent);
          z-index: -1;
        }
        
        .objective-number {
          font-weight: bold;
          font-size: 1.1rem;
        }
        
        .achievement-icon {
          flex-shrink: 0;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .display-4 {
            font-size: 2.5rem;
          }
          
          .fs-5 {
            font-size: 1rem !important;
          }
        }
        
        /* Animation for loading */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .card, h1, h2, .lead {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </>
  );
};

export default About;